import { Divider, NoStyleButton, Typography } from "@fullscript/aviary-web";
import { faAngleLeft as faAngleLeftLight } from "@fullscript/pro-light-svg-icons";
import { faAngleLeft, faLock, faTimes } from "@fullscript/pro-regular-svg-icons";
import { Children, isValidElement, type ReactElement, type ReactNode } from "react";

import { Stepper } from "@aviary";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { Container } from "@shared/layouts";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";

import { Back, type BackProps, type BackMobileVariant } from "./Back";
import { SecureBadge, type SecureBadgeProps } from "./SecureBadge";
import { Steps, type StepsProps } from "./Steps";

import * as styles from "./CheckoutProgressHeader.styles";

/**
 * Presentational, flow-agnostic checkout progress header.
 *
 * Compound component: consumers compose `<CheckoutProgressHeader.Back />`,
 * `<CheckoutProgressHeader.Steps />` and `<CheckoutProgressHeader.SecureBadge />`
 * and the root owns the shared frame + the responsive dual-placement of the
 * stepper (header row on desktop, below the divider on mobile).
 *
 * Strictly presentational by contract: no flippers, no routing, no i18n, no
 * network calls — every variable is a prop. Flow-specific behavior (step
 * lists, back semantics, labels) is supplied by thin per-surface wrappers
 * reading the selfCheckoutFlow descriptor.
 *
 * DS5 (`@fullscript/aviary-web`) throughout, except the DS4 `Stepper` — it
 * has no DS5 equivalent yet; its migration is tracked in BUT-1186.
 */

interface Props {
  children: ReactNode;
}

/**
 * Slot contract: the root reads the FIRST direct child of each slot type
 * (`.Back` / `.Steps` / `.SecureBadge`) and renders it into the frame itself.
 * Duplicate slots are ignored (first wins), unknown or nested children are
 * dropped — slots must be direct children, not wrapped in fragments or
 * intermediate elements.
 */
const findSlot = <P,>(children: ReactNode, slotType: (props: P) => null): P | undefined => {
  const match = Children.toArray(children).find(
    (child): child is ReactElement<P> => isValidElement(child) && child.type === slotType
  );

  return match?.props;
};

const CheckoutProgressHeader = ({ children }: Props) => {
  const { tablet } = useBreakpoints();
  const back = findSlot<BackProps>(children, Back);
  const steps = findSlot<StepsProps>(children, Steps);
  const secureBadge = findSlot<SecureBadgeProps>(children, SecureBadge);
  const isDesktop = !!tablet.greaterThan;

  const renderBack = () => {
    if (!back) return <div />;

    if (!isDesktop) {
      const icon = back.mobileVariant === "close" ? faTimes : faAngleLeft;

      return (
        <div css={styles.backWrapper}>
          <NoStyleButton
            onClick={back.onBack}
            aria-label={back.label}
            customCss={styles.backButtonMobile}
          >
            <FontAwesomeIcon size="lg" icon={icon} />
          </NoStyleButton>
        </div>
      );
    }

    return (
      <div css={styles.backWrapper}>
        <NoStyleButton onClick={back.onBack} customCss={styles.backButton}>
          <FontAwesomeIcon size="lg" icon={faAngleLeftLight} />
          {back.label}
        </NoStyleButton>
      </div>
    );
  };

  const renderSteps = () => {
    if (!steps) return null;

    return (
      <div css={isDesktop ? styles.stepperWrapper : styles.mobileStepperWrapper}>
        <Stepper
          steps={steps.steps.map(({ label }) => ({ label }))}
          currentStep={steps.currentStep}
          isIncompleteFinalStep
          css={styles.stepperNav}
        />
      </div>
    );
  };

  const renderSecureBadge = () => {
    if (!secureBadge) return <div />;

    // Renders as a non-heading (the old headers used <h1>): the badge is
    // chrome, not the page heading — the real h1 belongs to the page content.
    return (
      <Typography fontWeight="bold" isMarginless customCss={styles.secureBadge}>
        <FontAwesomeIcon size="lg" icon={faLock} css={styles.secureIcon} />
        {secureBadge.label}
      </Typography>
    );
  };

  return (
    <>
      <header css={styles.header}>
        <Container css={styles.wrapper}>
          {renderBack()}
          {!!isDesktop && renderSteps()}
          {renderSecureBadge()}
        </Container>
      </header>
      <Divider />
      {!isDesktop && renderSteps()}
    </>
  );
};

CheckoutProgressHeader.Back = Back;
CheckoutProgressHeader.Steps = Steps;
CheckoutProgressHeader.SecureBadge = SecureBadge;

export { CheckoutProgressHeader };
export type { BackMobileVariant };
